import React, { useState } from 'react';
import { ServerIcon, PlusIcon, TrashIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, RefreshCwIcon, DownloadIcon, FilterIcon, SettingsIcon, DollarSignIcon, CalendarIcon, UsersIcon, BuildingIcon, TagIcon, AlertTriangleIcon, ZapIcon, ArrowUpIcon, ArrowDownIcon, ClockIcon, BellIcon, SearchIcon, ExternalLinkIcon, PlusCircleIcon, XIcon, CheckIcon, EyeIcon, EyeOffIcon, KeyIcon, ChevronLeftIcon } from 'lucide-react';
export function ModelManagement() {
  const [activeTab, setActiveTab] = useState('self-deployed');
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [showAddProviderModal, setShowAddProviderModal] = useState(false);
  const [addProviderStep, setAddProviderStep] = useState<1 | 2>(1);
  const [selectedProvider, setSelectedProvider] = useState<any>(null);
  const [newApiKey, setNewApiKey] = useState('');
  const [showNewApiKey, setShowNewApiKey] = useState(false);
  const [monthlyBudget, setMonthlyBudget] = useState(10000);
  const [alertThreshold, setAlertThreshold] = useState(15);
  const [isBudgetSet, setIsBudgetSet] = useState(false);
  // New provider form state
  const [newProviderData, setNewProviderData] = useState({
    providerId: '',
    apiKey: ''
  });
  // Available SaaS providers list
  const availableProviders = [{
    id: 'openai',
    name: 'OpenAI',
    description: 'GPT-4o, GPT-4 Turbo and GPT-3.5 models',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4d/OpenAI_Logo.svg/512px-OpenAI_Logo.svg.png',
    logoFallback: 'OA',
    color: '#10a37f',
    models: [{
      id: 'gpt-4o',
      name: 'GPT-4o',
      version: 'Latest',
      cost: '5.00',
      enabled: true
    }, {
      id: 'gpt-4-turbo',
      name: 'GPT-4 Turbo',
      version: '1106',
      cost: '10.00',
      enabled: true
    }, {
      id: 'gpt-3.5-turbo',
      name: 'GPT-3.5 Turbo',
      version: '0125',
      cost: '0.50',
      enabled: false
    }]
  }, {
    id: 'anthropic',
    name: 'Anthropic',
    description: 'Claude 3.5 Sonnet, Opus and Haiku models',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/78/Anthropic_logo.svg/512px-Anthropic_logo.svg.png',
    logoFallback: 'AN',
    color: '#d97706',
    models: [{
      id: 'claude-3.5-sonnet',
      name: 'Claude 3.5 Sonnet',
      version: 'Latest',
      cost: '3.00',
      enabled: true
    }, {
      id: 'claude-3-opus',
      name: 'Claude 3 Opus',
      version: '20240229',
      cost: '15.00',
      enabled: true
    }, {
      id: 'claude-3-haiku',
      name: 'Claude 3 Haiku',
      version: '20240307',
      cost: '0.25',
      enabled: false
    }]
  }, {
    id: 'google',
    name: 'Google AI',
    description: 'Gemini 2.0 Flash, 1.5 Pro and Flash models',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/53/Google_%22G%22_Logo.svg/512px-Google_%22G%22_Logo.svg.png',
    logoFallback: 'GG',
    color: '#4285f4',
    models: [{
      id: 'gemini-2.0-flash',
      name: 'Gemini 2.0 Flash',
      version: 'Latest',
      cost: '0.10',
      enabled: true
    }, {
      id: 'gemini-1.5-pro',
      name: 'Gemini 1.5 Pro',
      version: 'Latest',
      cost: '3.50',
      enabled: true
    }, {
      id: 'gemini-1.5-flash',
      name: 'Gemini 1.5 Flash',
      version: 'Latest',
      cost: '0.075',
      enabled: false
    }]
  }, {
    id: 'mistral',
    name: 'Mistral AI',
    description: 'Mistral Large, Small and Mixtral models',
    logo: 'https://avatars.githubusercontent.com/u/132333856?s=200&v=4',
    logoFallback: 'MI',
    color: '#f97316',
    models: [{
      id: 'mistral-large',
      name: 'Mistral Large',
      version: 'Latest',
      cost: '8.00',
      enabled: true
    }, {
      id: 'mistral-small',
      name: 'Mistral Small',
      version: 'Latest',
      cost: '1.00',
      enabled: true
    }, {
      id: 'mixtral-8x22b',
      name: 'Mixtral 8x22B',
      version: 'Latest',
      cost: '2.00',
      enabled: false
    }]
  }, {
    id: 'cohere',
    name: 'Cohere',
    description: 'Command R+ and Command R models',
    logo: 'https://asset.brandfetch.io/idHg4XZQJT/idMFLHxDDX.png',
    logoFallback: 'CO',
    color: '#39594d',
    models: [{
      id: 'command-r-plus',
      name: 'Command R+',
      version: 'Latest',
      cost: '3.00',
      enabled: true
    }, {
      id: 'command-r',
      name: 'Command R',
      version: 'Latest',
      cost: '0.50',
      enabled: true
    }]
  }, {
    id: 'xai',
    name: 'xAI',
    description: 'Grok-2 and Grok-2 Mini models',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/b2/X_logo_2023.svg/512px-X_logo_2023.svg.png',
    logoFallback: 'XA',
    color: '#ffffff',
    models: [{
      id: 'grok-2',
      name: 'Grok-2',
      version: 'Latest',
      cost: '10.00',
      enabled: true
    }, {
      id: 'grok-2-mini',
      name: 'Grok-2 Mini',
      version: 'Latest',
      cost: '2.00',
      enabled: true
    }]
  }, {
    id: 'groq',
    name: 'Groq',
    description: 'Ultra-fast inference for open-source models',
    logo: 'https://avatars.githubusercontent.com/u/116107061?s=200&v=4',
    logoFallback: 'GQ',
    color: '#f55036',
    models: [{
      id: 'llama-3.1-70b-groq',
      name: 'Llama 3.1 70B',
      version: 'Latest',
      cost: '0.59',
      enabled: true
    }, {
      id: 'mixtral-8x7b-groq',
      name: 'Mixtral 8x7B',
      version: 'Latest',
      cost: '0.24',
      enabled: true
    }, {
      id: 'gemma-7b-groq',
      name: 'Gemma 7B',
      version: 'Latest',
      cost: '0.07',
      enabled: false
    }]
  }, {
    id: 'perplexity',
    name: 'Perplexity',
    description: 'Online LLMs with real-time web search',
    logo: 'https://avatars.githubusercontent.com/u/110299016?s=200&v=4',
    logoFallback: 'PP',
    color: '#20808d',
    models: [{
      id: 'pplx-70b-online',
      name: 'pplx-70b-online',
      version: 'Latest',
      cost: '1.00',
      enabled: true
    }, {
      id: 'pplx-7b-online',
      name: 'pplx-7b-online',
      version: 'Latest',
      cost: '0.20',
      enabled: false
    }]
  }];
  // Self-deployed models data
  const [selfDeployedModels] = useState([{
    id: 'llama-4-8b',
    name: 'Llama-4-8B',
    description: 'Fast and efficient for most tasks',
    status: 'loaded',
    size: '8B',
    version: 'v4.0',
    family: 'Llama',
    context: '128k',
    cost: '1.20'
  }, {
    id: 'llama-4-70b',
    name: 'Llama-4-70B',
    description: 'High performance for complex tasks',
    status: 'available',
    size: '70B',
    version: 'v4.0',
    family: 'Llama',
    context: '128k',
    cost: '3.80'
  }, {
    id: 'mistral-7b',
    name: 'Mistral-7B',
    description: 'Optimized for speed and efficiency',
    status: 'loaded',
    size: '7B',
    version: 'v0.3',
    family: 'Mistral',
    context: '32k',
    cost: '0.95'
  }, {
    id: 'mixtral-8x7b',
    name: 'Mixtral-8x7B',
    description: 'Mixture of experts architecture',
    status: 'available',
    size: '8x7B',
    version: 'v0.1',
    family: 'Mistral',
    context: '32k',
    cost: '2.40'
  }]);
  // SaaS providers data
  const [saasProviders, setSaasProviders] = useState([{
    id: 'openai',
    name: 'OpenAI',
    description: 'GPT-4 and GPT-3.5 models',
    apiKey: '••••••••••••••••••••••••••••••••',
    models: [{
      id: 'gpt-4o',
      name: 'GPT-4o',
      version: 'Latest',
      cost: '5.00',
      enabled: true
    }, {
      id: 'gpt-4-turbo',
      name: 'GPT-4 Turbo',
      version: '1106',
      cost: '10.00',
      enabled: true
    }, {
      id: 'gpt-3.5-turbo',
      name: 'GPT-3.5 Turbo',
      version: '0125',
      cost: '0.50',
      enabled: false
    }]
  }, {
    id: 'anthropic',
    name: 'Anthropic',
    description: 'Claude 3 model family',
    apiKey: '••••••••••••••••••••••••••••••••',
    models: [{
      id: 'claude-3.5-sonnet',
      name: 'Claude-3.5 Sonnet',
      version: 'Latest',
      cost: '3.00',
      enabled: true
    }, {
      id: 'claude-3-opus',
      name: 'Claude-3 Opus',
      version: '20240229',
      cost: '15.00',
      enabled: true
    }, {
      id: 'claude-3-sonnet',
      name: 'Claude-3 Sonnet',
      version: '20240229',
      cost: '3.00',
      enabled: false
    }]
  }]);
  // Handler to toggle model enabled state
  const toggleModelEnabled = (providerId: string, modelId: string) => {
    setSaasProviders(prevProviders => prevProviders.map(provider => {
      if (provider.id === providerId) {
        return {
          ...provider,
          models: provider.models.map(model => {
            if (model.id === modelId) {
              return {
                ...model,
                enabled: !model.enabled
              };
            }
            return model;
          })
        };
      }
      return provider;
    }));
  };
  const handleConnectProvider = () => {
    if (!selectedProvider || !newApiKey) return;
    const newProvider = {
      id: selectedProvider.id,
      name: selectedProvider.name,
      description: selectedProvider.description,
      apiKey: '••••••••••••••••••••••••••••••••',
      models: selectedProvider.models
    };
    setSaasProviders(prev => [...prev, newProvider]);
    setShowAddProviderModal(false);
    setAddProviderStep(1);
    setSelectedProvider(null);
    setNewApiKey('');
    setShowNewApiKey(false);
  };
  const openAddProvider = () => {
    setAddProviderStep(1);
    setSelectedProvider(null);
    setNewApiKey('');
    setShowNewApiKey(false);
    setShowAddProviderModal(true);
  };
  return <div className="space-y-6 text-white" data-id="element-1634">
      <h2 className="text-2xl font-bold text-white" data-id="element-1635">Model Management</h2>
      {/* Tabs */}
      <div className="border-b border-[#222222]" data-id="element-1636">
        <div className="flex" data-id="element-1637">
          <button onClick={() => setActiveTab('self-deployed')} className={`flex items-center py-3 px-4 border-b-2 ${activeTab === 'self-deployed' ? 'border-blue-500 text-blue-400' : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'}`} data-id="element-1638">
            <ServerIcon size={18} className="mr-2" data-id="element-1639" />
            Self Deployed Models
          </button>
          <button onClick={() => setActiveTab('saas')} className={`flex items-center py-3 px-4 border-b-2 ${activeTab === 'saas' ? 'border-blue-500 text-blue-400' : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'}`} data-id="element-1640">
            <ExternalLinkIcon size={18} className="mr-2" data-id="element-1641" />
            SaaS Models
          </button>
        </div>
      </div>
      {/* Self Deployed Models Tab */}
      {activeTab === 'self-deployed' && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1642">
          {selfDeployedModels.map(model => <div key={model.id} className="bg-[#0F0F0F] rounded-lg border border-[#222222] overflow-hidden" data-id="element-1643">
              <div className="p-4" data-id="element-1644">
                <div className="flex justify-between items-start mb-2" data-id="element-1645">
                  <h3 className="text-lg font-medium text-white" data-id="element-1646">
                    {model.name}
                  </h3>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${model.status === 'loaded' ? 'bg-green-900 text-green-300 border border-green-800' : 'bg-blue-900 text-blue-300 border border-blue-800'}`} data-id="element-1647">
                    {model.status === 'loaded' ? 'Loaded' : 'Available'}
                  </span>
                </div>
                <p className="text-sm text-gray-400 mb-4" data-id="element-1648">
                  {model.description}
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm mb-4" data-id="element-1649">
                  <div data-id="element-1650">
                    <p className="text-gray-500" data-id="element-1651">Size:</p>
                    <p className="font-medium text-white" data-id="element-1652">{model.size}</p>
                  </div>
                  <div data-id="element-1653">
                    <p className="text-gray-500" data-id="element-1654">Version:</p>
                    <p className="font-medium text-white" data-id="element-1655">{model.version}</p>
                  </div>
                  <div data-id="element-1656">
                    <p className="text-gray-500" data-id="element-1657">Family:</p>
                    <p className="font-medium text-white" data-id="element-1658">{model.family}</p>
                  </div>
                  <div data-id="element-1659">
                    <p className="text-gray-500" data-id="element-1660">Context:</p>
                    <p className="font-medium text-white" data-id="element-1661">{model.context}</p>
                  </div>
                  <div className="col-span-2" data-id="element-1662">
                    <p className="text-gray-500 flex items-center" data-id="element-1663">
                      <DollarSignIcon size={14} className="mr-1" data-id="element-1664" /> $ / 1M
                      Tokens (Avg):
                    </p>
                    <p className="font-medium text-white" data-id="element-1665">${model.cost}</p>
                  </div>
                </div>
                <div className="flex space-x-2" data-id="element-1666">
                  {model.status === 'loaded' ? <button className="flex-1 py-1.5 px-3 bg-[#121212] border border-red-800 text-red-400 rounded-md hover:bg-red-900/30 text-sm font-medium" data-id="element-1667">
                      Unload
                    </button> : <>
                      <button className="flex-1 py-1.5 px-3 bg-[#121212] border border-blue-800 text-blue-400 rounded-md hover:bg-blue-900/30 text-sm font-medium" data-id="element-1668">
                        Load
                      </button>
                      <button className="flex-1 py-1.5 px-3 bg-[#121212] border border-purple-800 text-purple-400 rounded-md hover:bg-purple-900/30 text-sm font-medium" data-id="element-1669">
                        Upgrade
                      </button>
                    </>}
                </div>
              </div>
            </div>)}
        </div>}
      {/* SaaS Models Tab */}
      {activeTab === 'saas' && <div data-id="element-1670">
          <div className="mb-6 bg-[#0F0F0F] rounded-lg border border-[#222222] p-4" data-id="element-1671">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-1672">
              <div className="mb-4 md:mb-0" data-id="element-1673">
                <h3 className="text-base font-medium text-white flex items-center" data-id="element-1674">
                  <DollarSignIcon size={18} className="mr-2 text-green-400" data-id="element-1675" />
                  Monthly SaaS Budget
                </h3>
                {isBudgetSet ? <div className="mt-2 space-y-1" data-id="element-1676">
                    <div className="flex items-center" data-id="element-1677">
                      <span className="text-sm text-gray-400 mr-2" data-id="element-1678">
                        Budget:
                      </span>
                      <span className="text-base font-medium text-white" data-id="element-1679">
                        ${monthlyBudget.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center" data-id="element-1680">
                      <span className="text-sm text-gray-400 mr-2" data-id="element-1681">
                        Alert at:
                      </span>
                      <span className="text-sm font-medium px-2 py-1 bg-yellow-900/30 border border-yellow-800 text-yellow-300 rounded-md flex items-center" data-id="element-1682">
                        <BellIcon size={12} className="mr-1" data-id="element-1683" />
                        {alertThreshold}% of budget
                      </span>
                    </div>
                  </div> : <p className="mt-2 text-sm text-gray-400" data-id="element-1684">
                    No monthly budget set. Set a budget to monitor and control
                    SaaS model costs.
                  </p>}
              </div>
              <button onClick={() => setShowBudgetModal(true)} className="flex items-center px-4 py-2 bg-[#121212] border border-green-800 text-green-400 rounded-md hover:bg-green-900/30 text-sm font-medium" data-id="element-1685">
                <DollarSignIcon size={16} className="mr-2" data-id="element-1686" />
                {isBudgetSet ? 'Edit Budget' : 'Set Monthly Budget'}
              </button>
            </div>
          </div>
          <div className="flex justify-between items-center mb-4" data-id="element-1687">
            <button onClick={openAddProvider} className="flex items-center text-blue-400 font-medium hover:text-blue-300" data-id="element-1688">
              <PlusCircleIcon size={18} className="mr-1" data-id="element-1689" />
              Add Provider
            </button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1690">
            {saasProviders.map(provider => <div key={provider.id} className="bg-[#0F0F0F] rounded-lg border border-[#222222] overflow-hidden" data-id="element-1691">
                <div className="p-4" data-id="element-1692">
                  <div className="flex justify-between items-start mb-2" data-id="element-1693">
                    <div data-id="element-1694">
                      <h3 className="text-lg font-medium text-white" data-id="element-1695">
                        {provider.name}
                      </h3>
                      <p className="text-sm text-gray-400" data-id="element-1696">
                        {provider.description}
                      </p>
                    </div>
                    <div className="flex items-center" data-id="element-1697">
                      <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-900 text-green-300 mr-2 border border-green-800" data-id="element-1698">
                        Connected
                      </span>
                      <button className="text-gray-400 hover:text-gray-300" data-id="element-1699">
                        <XIcon size={16} data-id="element-1700" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 mb-4" data-id="element-1701">
                    <label className="flex items-center text-sm text-gray-400 mb-1" data-id="element-1702">
                      <ExternalLinkIcon size={14} className="mr-1" data-id="element-1703" />
                      API Key
                    </label>
                    <div className="flex space-x-2" data-id="element-1704">
                      <input type="password" value={provider.apiKey} readOnly className="flex-1 border border-[#333333] bg-[#121212] rounded-md px-3 py-2 text-sm text-gray-300" data-id="element-1705" />
                      <button className="px-3 py-2 bg-[#121212] border border-red-800 text-red-400 rounded-md hover:bg-red-900/30 text-sm font-medium" data-id="element-1706">
                        Disconnect
                      </button>
                    </div>
                  </div>
                  <div data-id="element-1707">
                    <div className="flex justify-between items-center mb-2" data-id="element-1708">
                      <h4 className="text-sm font-medium text-gray-300" data-id="element-1709">
                        Available Models
                      </h4>
                      <span className="text-xs text-gray-500" data-id="element-1710">
                        Toggle to enable/disable
                      </span>
                    </div>
                    <div className="space-y-2" data-id="element-1711">
                      <div className="flex justify-between items-center py-1.5 border-b border-[#222222] text-xs text-gray-500 font-medium" data-id="element-1712">
                        <div className="flex-1" data-id="element-1713">Model</div>
                        <div className="flex-1 text-center" data-id="element-1714">Version</div>
                        <div className="flex-1 text-center flex items-center justify-center" data-id="element-1715">
                          <DollarSignIcon size={12} className="mr-1" data-id="element-1716" /> $ / 1M
                          Tokens (Avg)
                        </div>
                        <div className="w-16 text-right" data-id="element-1717">Status</div>
                      </div>
                      {provider.models.map(model => <div key={model.id} className="flex justify-between items-center py-1.5 border-b border-[#222222]" data-id="element-1718">
                          <div className="flex-1" data-id="element-1719">
                            <span className="text-sm font-medium text-white" data-id="element-1720">
                              {model.name}
                            </span>
                          </div>
                          <div className="flex-1 text-center" data-id="element-1721">
                            <span className="text-xs text-gray-400" data-id="element-1722">
                              {model.version}
                            </span>
                          </div>
                          <div className="flex-1 text-center" data-id="element-1723">
                            <span className="text-sm font-medium text-white" data-id="element-1724">
                              ${model.cost}
                            </span>
                          </div>
                          <div className="w-16 text-right" data-id="element-1725">
                            <div className="relative inline-block w-10 mr-2 align-middle select-none" data-id="element-1726">
                              <input type="checkbox" id={`toggle-${provider.id}-${model.id}`} checked={model.enabled} onChange={() => toggleModelEnabled(provider.id, model.id)} className="sr-only" data-id="element-1727" />
                              <label htmlFor={`toggle-${provider.id}-${model.id}`} className={`block h-6 rounded-full cursor-pointer ${model.enabled ? 'bg-green-600' : 'bg-gray-700'}`} data-id="element-1728">
                                <span className={`absolute left-0 top-0 h-6 w-6 rounded-full border border-gray-600 bg-[#0F0F0F] transition-transform transform ${model.enabled ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-1729"></span>
                              </label>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </div>
                </div>
              </div>)}
          </div>
        </div>}
      {/* Monthly Budget Modal */}
      {showBudgetModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-1730">
          <div className="bg-[#0F0F0F] rounded-lg shadow-xl max-w-md w-full border border-[#333333]" data-id="element-1731">
            <div className="flex items-center justify-between border-b border-[#222222] px-6 py-4" data-id="element-1732">
              <h3 className="text-lg font-medium text-white flex items-center" data-id="element-1733">
                <DollarSignIcon size={18} className="mr-2 text-green-400" data-id="element-1734" />
                Set Monthly SaaS Budget
              </h3>
              <button onClick={() => setShowBudgetModal(false)} className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-[#1a1a1a] transition-colors duration-200" data-id="element-1735">
                <XIcon size={20} data-id="element-1736" />
              </button>
            </div>
            <div className="px-6 py-4" data-id="element-1737">
              <div className="space-y-4" data-id="element-1738">
                <div data-id="element-1739">
                  <label className="block text-sm font-medium text-gray-300 mb-2" data-id="element-1740">
                    Monthly Budget Limit (USD)
                  </label>
                  <div className="relative" data-id="element-1741">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1742">
                      <span className="text-gray-400" data-id="element-1743">$</span>
                    </div>
                    <input type="number" value={monthlyBudget} onChange={e => setMonthlyBudget(Math.max(0, Number(e.target.value)))} className="block w-full pl-8 pr-4 py-2 bg-[#121212] border border-[#333333] rounded-md text-white focus:ring-blue-500 focus:border-blue-500" placeholder="Enter budget amount" min="0" data-id="element-1744" />
                  </div>
                </div>
                <div data-id="element-1745">
                  <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center" data-id="element-1746">
                    <BellIcon size={16} className="mr-2 text-yellow-400" data-id="element-1747" />
                    Alert Threshold
                  </label>
                  <div className="flex items-center" data-id="element-1748">
                    <input type="range" min="5" max="95" step="5" value={alertThreshold} onChange={e => setAlertThreshold(Number(e.target.value))} className="w-full h-2 bg-[#222] rounded-lg appearance-none cursor-pointer" data-id="element-1749" />
                    <span className="ml-3 text-white font-medium w-12" data-id="element-1750">
                      {alertThreshold}%
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-400" data-id="element-1751">
                    You will receive an alert when {alertThreshold}% of your
                    monthly budget is consumed
                  </p>
                </div>
                <div className="mt-4 bg-[#121212] p-3 rounded-md text-xs border border-[#333333] text-gray-300" data-id="element-1752">
                  <div className="flex items-center mb-1" data-id="element-1753">
                    <AlertTriangleIcon size={14} className="text-amber-400 mr-1" data-id="element-1754" />
                    <span className="font-medium" data-id="element-1755">Note:</span>
                  </div>
                  <p data-id="element-1756">
                    This budget applies to all SaaS model providers. You can set
                    individual budgets for users in the Access Control section.
                  </p>
                </div>
              </div>
            </div>
            <div className="border-t border-[#222222] px-6 py-4 flex justify-end space-x-3" data-id="element-1757">
              <button onClick={() => setShowBudgetModal(false)} className="px-4 py-2 border border-[#333333] rounded-md text-gray-300 hover:bg-[#1a1a1a] transition-colors duration-200" data-id="element-1758">
                Cancel
              </button>
              <button onClick={() => {
            // In a real app, this would save the budget settings
            setIsBudgetSet(true);
            setShowBudgetModal(false);
          }} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 flex items-center" data-id="element-1759">
                <CheckIcon size={16} className="mr-1.5" data-id="element-1760" />
                Save Settings
              </button>
            </div>
          </div>
        </div>}

      {/* Add Provider Modal */}
      {showAddProviderModal && <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50" data-id="element-1761">
          <div className="bg-[#0F0F0F] rounded-lg shadow-xl border border-[#333333] w-full max-w-2xl max-h-[85vh] flex flex-col" data-id="element-1762">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-[#222222] px-6 py-4 flex-shrink-0" data-id="element-1763">
              <div className="flex items-center" data-id="element-1764">
                {addProviderStep === 2 && <button onClick={() => setAddProviderStep(1)} className="mr-3 text-gray-400 hover:text-white transition-colors" data-id="element-1765">
                    <ChevronLeftIcon size={20} data-id="element-1766" />
                  </button>}
                <h3 className="text-lg font-medium text-white flex items-center" data-id="element-1767">
                  <PlusCircleIcon size={18} className="mr-2 text-blue-400" data-id="element-1768" />
                  {addProviderStep === 1 ? 'Choose a Provider' : `Connect ${selectedProvider?.name}`}
                </h3>
              </div>
              <div className="flex items-center space-x-3" data-id="element-1769">
                <div className="flex items-center space-x-1.5" data-id="element-1770">
                  <div className={`h-2 w-2 rounded-full ${addProviderStep >= 1 ? 'bg-blue-500' : 'bg-[#333]'}`} data-id="element-1771" />
                  <div className={`h-2 w-2 rounded-full ${addProviderStep >= 2 ? 'bg-blue-500' : 'bg-[#333]'}`} data-id="element-1772" />
                </div>
                <button onClick={() => setShowAddProviderModal(false)} className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-[#1a1a1a] transition-colors" data-id="element-1773">
                  <XIcon size={20} data-id="element-1774" />
                </button>
              </div>
            </div>

            {/* Step 1: Provider Grid */}
            {addProviderStep === 1 && <div className="overflow-y-auto flex-1 p-6" data-id="element-1775">
                <p className="text-sm text-gray-400 mb-4" data-id="element-1776">
                  Select an AI provider to connect via API key.
                </p>
                <div className="grid grid-cols-2 gap-3" data-id="element-1777">
                  {availableProviders.map(provider => {
              const isConnected = saasProviders.some(p => p.id === provider.id);
              return <button key={provider.id} onClick={() => {
                if (!isConnected) {
                  setSelectedProvider(provider);
                  setAddProviderStep(2);
                }
              }} disabled={isConnected} className={`text-left p-4 rounded-lg border-2 transition-all duration-150 ${isConnected ? 'border-[#222222] opacity-50 cursor-not-allowed bg-[#0a0a0a]' : 'border-[#333333] hover:border-blue-500 hover:bg-blue-900/10 cursor-pointer bg-[#121212]'}`} data-id="element-1778">
                        <div className="flex items-start justify-between mb-2" data-id="element-1779">
                          <div className="flex items-center" data-id="element-1780">
                            <div className="w-8 h-8 rounded-md flex items-center justify-center mr-3 flex-shrink-0 overflow-hidden" style={{
                      backgroundColor: provider.color + '22',
                      border: `1px solid ${provider.color}44`
                    }} data-id="element-1781">
                              <img src={provider.logo} alt={provider.name} className="w-5 h-5 object-contain" onError={e => {
                        const t = e.currentTarget;
                        t.style.display = 'none';
                        if (t.nextElementSibling) (t.nextElementSibling as HTMLElement).style.display = 'flex';
                      }} data-id="element-1782" />
                              <span className="text-xs font-bold hidden items-center justify-center w-full h-full" style={{
                        color: provider.color
                      }} data-id="element-1783">
                                {provider.logoFallback}
                              </span>
                            </div>
                            <span className="font-medium text-white text-sm" data-id="element-1784">
                              {provider.name}
                            </span>
                          </div>
                          {isConnected && <span className="text-xs px-2 py-0.5 bg-green-900/50 text-green-400 border border-green-800 rounded-full flex-shrink-0" data-id="element-1785">
                              Connected
                            </span>}
                        </div>
                        <p className="text-xs text-gray-400 leading-relaxed" data-id="element-1786">
                          {provider.description}
                        </p>
                        <p className="text-xs text-gray-600 mt-2" data-id="element-1787">
                          {provider.models.length} models available
                        </p>
                      </button>;
            })}
                </div>
              </div>}

            {/* Step 2: API Key Entry */}
            {addProviderStep === 2 && selectedProvider && <div className="overflow-y-auto flex-1 p-6 space-y-5" data-id="element-1788">
                {/* Provider header */}
                <div className="flex items-center p-4 bg-[#121212] rounded-lg border border-[#222222]" data-id="element-1789">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 overflow-hidden" style={{
              backgroundColor: selectedProvider.color + '22',
              border: `1px solid ${selectedProvider.color}44`
            }} data-id="element-1790">
                    <img src={selectedProvider.logo} alt={selectedProvider.name} className="w-6 h-6 object-contain" data-id="element-1791" />
                  </div>
                  <div data-id="element-1792">
                    <p className="font-medium text-white" data-id="element-1793">
                      {selectedProvider.name}
                    </p>
                    <p className="text-xs text-gray-400" data-id="element-1794">
                      {selectedProvider.description}
                    </p>
                  </div>
                </div>

                {/* API Key input */}
                <div data-id="element-1795">
                  <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center" data-id="element-1796">
                    <KeyIcon size={14} className="mr-1.5 text-gray-400" data-id="element-1797" />
                    API Key
                  </label>
                  <div className="flex" data-id="element-1798">
                    <input type={showNewApiKey ? 'text' : 'password'} value={newApiKey} onChange={e => setNewApiKey(e.target.value)} className="flex-1 px-3 py-2 bg-[#121212] border border-[#333333] rounded-l-md text-white focus:ring-blue-500 focus:border-blue-500 placeholder-gray-600 text-sm" placeholder={`Enter your ${selectedProvider.name} API key...`} autoFocus data-id="element-1799" />
                    <button type="button" onClick={() => setShowNewApiKey(!showNewApiKey)} className="px-3 bg-[#1a1a1a] border-t border-r border-b border-[#333333] rounded-r-md text-gray-400 hover:text-white transition-colors" data-id="element-1800">
                      {showNewApiKey ? <EyeOffIcon size={16} data-id="element-1801" /> : <EyeIcon size={16} data-id="element-1802" />}
                    </button>
                  </div>
                  <p className="mt-1.5 text-xs text-gray-500" data-id="element-1803">
                    Your API key is stored securely and never shared.
                  </p>
                </div>

                {/* Models preview */}
                <div data-id="element-1804">
                  <h4 className="text-sm font-medium text-gray-300 mb-2" data-id="element-1805">
                    Models that will be added
                  </h4>
                  <div className="bg-[#121212] rounded-lg border border-[#222222] overflow-hidden" data-id="element-1806">
                    <div className="flex items-center px-4 py-2 border-b border-[#222222] text-xs text-gray-500 font-medium" data-id="element-1807">
                      <span className="flex-1" data-id="element-1808">Model</span>
                      <span className="w-20 text-center" data-id="element-1809">Version</span>
                      <span className="w-28 text-right" data-id="element-1810">$ / 1M Tokens</span>
                    </div>
                    {selectedProvider.models.map((model: any) => <div key={model.id} className="flex items-center px-4 py-2.5 border-b border-[#1a1a1a] last:border-0" data-id="element-1811">
                        <span className="flex-1 text-sm text-white" data-id="element-1812">
                          {model.name}
                        </span>
                        <span className="w-20 text-center text-xs text-gray-400" data-id="element-1813">
                          {model.version}
                        </span>
                        <span className="w-28 text-right text-sm font-medium text-white" data-id="element-1814">
                          ${model.cost}
                        </span>
                      </div>)}
                  </div>
                </div>
              </div>}

            {/* Footer */}
            <div className="border-t border-[#222222] px-6 py-4 flex justify-end space-x-3 flex-shrink-0" data-id="element-1815">
              <button onClick={() => setShowAddProviderModal(false)} className="px-4 py-2 border border-[#333333] rounded-md text-gray-300 hover:bg-[#1a1a1a] transition-colors text-sm" data-id="element-1816">
                Cancel
              </button>
              {addProviderStep === 2 && <button onClick={handleConnectProvider} disabled={!newApiKey.trim()} className={`px-4 py-2 rounded-md text-white transition-colors text-sm flex items-center ${!newApiKey.trim() ? 'bg-blue-600/40 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`} data-id="element-1817">
                  <CheckIcon size={15} className="mr-1.5" data-id="element-1818" />
                  Connect {selectedProvider?.name}
                </button>}
            </div>
          </div>
        </div>}
    </div>;
}